<template>
  <el-dialog
    title="选择报名人"
    :visible.sync="dialogVisible"
    width="1000px"
    :close-on-click-modal="false"
    @close="handleClose"
    append-to-body
  >
    <div class="form-section select-registrant-body">
      <el-button
        type="primary"
        size="small"
        class="mb10"
        @click="handleAddPerson"
        >新增报名人</el-button
      >
      <el-table
        :data="candidates"
        border
        size="small"
        :max-height="420"
        @selection-change="handleSelectionChange"
        style="width: 100%"
        ref="registrantTable"
        :row-key="getRowKey"
      >
        <el-table-column
          type="selection"
          width="50"
          :selectable="checkSelectable"
        />
        <el-table-column
          label="姓名"
          prop="realName"
          align="center"
          width="100px"
        />
        <el-table-column label="性别" prop="sex" align="center">
          <template slot-scope="{ row }">
            {{ row.sex === 1 ? "男" : row.sex === 2 ? "女" : "" }}
          </template>
        </el-table-column>
        <el-table-column
          label="与报名者关系"
          prop="relationship"
          align="center"
        />
        <el-table-column
          label="身份证号码"
          prop="idCardNo"
          align="center"
          width="200"
        >
          <template slot-scope="{ row }">
            <span>{{row.idCardNo}}</span>
            <span v-if="row.isChild" style="color: #999; margin-left: 5px;"
              >(儿童)</span
            >
            <span v-else style="color: #999; margin-left: 5px;">(成人)</span>
          </template>
        </el-table-column>
        <el-table-column
          label="手机号"
          prop="mobile"
          align="center"
        />
        <el-table-column label="操作" align="center" width="100">
          <template slot-scope="{ $index }">
            <el-button type="text" @click="handleEdit($index)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确认</el-button>
    </span>

    <!-- 新增/编辑报名人表单弹窗 -->
    <person-form-dialog
      :visible.sync="personFormVisible"
      :title="personFormTitle"
      :form-data="editingPerson"
      :user-id="userId"
      :user-info-id="userInfoId"
      :user-type="userType"
      @confirm="handlePersonFormConfirm"
    />
  </el-dialog>
</template>

<script>
import PersonFormDialog from "./PersonFormDialog.vue";
import meetingCarryPeopleApi from "@/api/meeting/meetingCarryPeople";

export default {
  name: "SelectRegistrantDialog",
  components: {
    PersonFormDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    meetingId: {
      type: [Number, String],
      default: null
    },
    userId: {
      type: [Number, String],
      default: null
    },
    userInfoId: {
      type: [Number, String],
      default: null
    },
    userType: {
      type: Number,
      default: null
    },
    phone: {
      type: String,
      default: ""
    },
    existingRegistrants: {
      type: Array,
      default: () => []
    },
    maxCarryPeople: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      candidates: [],
      selectedRows: [],
      personFormVisible: false,
      personFormTitle: "新建报名人",
      editingPerson: {},
      editingIndex: null
    };
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal;
      if (newVal) {
        this.loadCandidates();
      }
    },
    dialogVisible(newVal) {
      this.$emit("update:visible", newVal);
    }
  },
  methods: {
    // 获取行的唯一标识（用于 row-key）
    getRowKey(row) {
      // 如果 id 不为 null，使用 id；否则使用 idCardNo + realName 组合
      if (row.id !== null && row.id !== undefined) {
        return `id_${row.id}`;
      }
      return `temp_${row.idCardNo}_${row.realName}`;
    },
    // 判断是否是本人（id为null且relationship为"本人"）
    isSelf(row) {
      return row && row.id === null && row.relationship === "本人";
    },
    // 格式化身份证显示
    formatIdCard(idCard) {
      if (!idCard) return "";
      if (idCard.length <= 8) return idCard;
      const start = idCard.substring(0, 3);
      const end = idCard.substring(idCard.length - 4);
      return `${start}${"*".repeat(idCard.length - 7)}${end}`;
    },
    // 加载候选列表
    async loadCandidates() {
      this.selectedRows = [];
      try {
        const res = await meetingCarryPeopleApi.list({
          pageNo: 1,
          pageSize: 100,
          isChild: false,
          userId: this.userId,
          meetingId: this.meetingId,
          userInfoId: this.userInfoId,
          userType: this.userType
        });
        const list =
          (res &&
            (res.data && res.data.list ? res.data.list : res.data || res)) ||
          [];
        const mapped = (list || []).map((it, index) => ({
          id: it.id !== undefined ? it.id : null, // 保持后端返回的null值，不要重新赋值
          realName: it.realName || "",
          sex: it.sex,
          relationship: it.relationship || "",
          idCardNo: it.idCardNo || "",
          mobile: it.mobile || "",
          isChild: !!it.isChild,
          userId: it.userId || null,
          userInfoId: it.userInfoId || null,
          userType: it.userType || null
        }));
        this.candidates = mapped;
        
        // 根据 existingRegistrants 回显已选择的参会人
        this.restoreSelectedRows();
      } catch (e) {
        this.candidates = [];
      }
    },
    // 恢复已选择的参会人（回显）
    restoreSelectedRows() {
      if (!this.existingRegistrants || this.existingRegistrants.length === 0) {
        return;
      }
      
      // 根据 existingRegistrants 匹配 candidates，找到对应的行
      const matchedRows = [];
      this.existingRegistrants.forEach(existing => {
        const candidate = this.candidates.find(c => {
          // 如果有 id，使用 id 匹配
          if (existing.id !== null && existing.id !== undefined && c.id !== null && c.id !== undefined) {
            return c.id === existing.id;
          }
          // 如果没有 id，使用 idCardNo + realName 匹配
          return c.idCardNo === existing.idCardNo && c.realName === existing.realName;
        });
        
        if (candidate) {
          matchedRows.push(candidate);
        }
      });
      
      // 设置 selectedRows
      this.selectedRows = matchedRows;
      
      // 在下一个 tick 勾选表格中的行
      this.$nextTick(() => {
        if (this.$refs.registrantTable && matchedRows.length > 0) {
          matchedRows.forEach(row => {
            this.$refs.registrantTable.toggleRowSelection(row, true);
          });
        }
      });
    },
    // 检查行是否可选
    checkSelectable(row) {
      // 如果当前行已经是选中的，则允许取消选择
      const isSelected = this.selectedRows.some(
        r => 
          (r.id !== null && r.id === row.id && r.id !== undefined) ||
          (r.id === null && row.id === null && 
           r.idCardNo === row.idCardNo && 
           r.realName === row.realName)
      );
      if (isSelected) {
        return true;
      }

      // "本人"永远可以选择，不受 maxCarryPeople 限制
      if (this.isSelf(row)) {
        return true;
      }

      // 对于非本人，检查 maxCarryPeople 限制
      if (this.maxCarryPeople === null || this.maxCarryPeople === undefined) {
        return true;
      }

      // 计算当前已选择的非本人数量（不包括本人）
      const nonSelfCount = this.selectedRows.filter(r => !this.isSelf(r)).length;
      
      // 非本人数量限制 = maxCarryPeople
      // 如果非本人数量已达到 maxCarryPeople，不允许再选择非本人
      return nonSelfCount < this.maxCarryPeople;
    },
    // 选择变化
    handleSelectionChange(rows) {
      // 检查非本人数量是否超过限制（本人不在限制内）
      if (
        this.maxCarryPeople !== null &&
        this.maxCarryPeople !== undefined
      ) {
        // 计算非本人数量（不包括本人）
        const nonSelfRows = rows.filter(r => !this.isSelf(r));
        const nonSelfCount = nonSelfRows.length;

        if (nonSelfCount > this.maxCarryPeople) {
          // 如果超过限制（可能是全选导致的），保留所有本人，但只保留前 maxCarryPeople 个非本人
          this.$message.warning(
            `最多只能选择 ${this.maxCarryPeople} 个非本人人员，已自动选择前 ${this.maxCarryPeople} 个`
          );
          
          // 分离本人和非本人
          const selfRows = rows.filter(r => this.isSelf(r));
          const limitedNonSelfRows = nonSelfRows.slice(0, this.maxCarryPeople);
          
          // 合并：所有本人 + 前 maxCarryPeople 个非本人
          const limitedRows = [...selfRows, ...limitedNonSelfRows];
          
          // 更新 selectedRows
          this.selectedRows = limitedRows;
          
          // 同步表格状态，取消超出限制的行的选择
          this.$nextTick(() => {
            if (this.$refs.registrantTable) {
              // 先清空所有选择
              this.$refs.registrantTable.clearSelection();
              // 重新选择：所有本人 + 前 maxCarryPeople 个非本人
              limitedRows.forEach(row => {
                const candidate = this.candidates.find(
                  c => 
                    (c.id !== null && c.id === row.id && c.id !== undefined) ||
                    (c.id === null && row.id === null && 
                     c.idCardNo === row.idCardNo && 
                     c.realName === row.realName)
                );
                if (candidate) {
                  this.$refs.registrantTable.toggleRowSelection(candidate, true);
                }
              });
            }
          });
          return;
        }
      }

      this.selectedRows = rows;
      
      // 强制组件重新渲染，确保所有行的 selectable 重新计算
      // 使用 $forceUpdate 强制 Vue 重新渲染组件，这会触发所有行的 selectable 重新评估
      this.$nextTick(() => {
        this.$forceUpdate();
        // 恢复选择状态（forceUpdate 可能会清空选择）
        if (this.$refs.registrantTable && this.selectedRows && this.selectedRows.length > 0) {
          this.$nextTick(() => {
            this.selectedRows.forEach(selectedRow => {
              const candidate = this.candidates.find(
                c => 
                  (c.id !== null && c.id === selectedRow.id && c.id !== undefined) ||
                  (c.id === null && selectedRow.id === null && 
                   c.idCardNo === selectedRow.idCardNo && 
                   c.realName === selectedRow.realName)
              );
              if (candidate) {
                this.$refs.registrantTable.toggleRowSelection(candidate, true);
              }
            });
          });
        }
      });
    },
    // 打开新增弹窗
    handleAddPerson() {
      this.personFormTitle = "新建报名人";
      this.editingPerson = {};
      this.editingIndex = null;
      this.personFormVisible = true;
    },
    // 打开编辑弹窗
    handleEdit(index) {
      this.personFormTitle = "编辑报名人";
      this.editingPerson = { ...this.candidates[index] };
      this.editingIndex = index;
      this.personFormVisible = true;
    },
    // 表单确认（新增或编辑时调用接口保存）
    async handlePersonFormConfirm(personData) {
      try {
        // 如果是新增且关系为"本人"，不允许创建（后端已返回id为null的"本人"数据）
        if (this.editingIndex === null && personData.relationship === "本人") {
          this.$message.warning("本人数据由后端提供，不允许新增");
          return;
        }

        // 如果是编辑，检查原记录是否是id为null的"本人"记录（后端返回的）
        if (this.editingIndex !== null) {
          const originalRecord = this.candidates[this.editingIndex];
          // 如果原记录是本人（id为null且relationship为"本人"），不允许保存（后端已提供，不允许修改）
          if (this.isSelf(originalRecord)) {
            this.$message.warning("本人数据由后端提供，不允许修改");
            return;
          }
        }

        // 构建接口所需的数据结构
        const apiData = {
          meetingId: this.meetingId || null,
          realName: personData.realName || "",
          mobile: personData.mobile || "",
          sex: personData.sex || null,
          relationship: personData.relationship || "",
          idCardNo: personData.idCardNo || "",
          isChild: personData.isChild || false,
          userId: personData.userId || this.userId || null,
          userInfoId: personData.userInfoId || this.userInfoId || null,
          userType: personData.userType || this.userType || null
        };

        // 调用接口保存
        await meetingCarryPeopleApi.create(apiData);

        // 保存成功后，添加到列表或更新列表，并关闭弹窗
        if (this.editingIndex !== null) {
          // 编辑模式：更新现有数据
          this.$set(this.candidates, this.editingIndex, personData);
          this.$message.success("编辑报名人成功");
        } else {
          // 新增模式：添加到列表
          this.candidates.push(personData);
          this.$message.success("新增报名人成功");
        }
        // 保存成功，关闭表单弹窗
        this.personFormVisible = false;
      } catch (error) {
        console.error("保存报名人失败:", error);
        this.$message.error(
          error.msg ||
            (this.editingIndex !== null ? "编辑报名人失败" : "新增报名人失败")
        );
        // 保存失败，不关闭弹窗，让用户修改后重试
      }
    },
    // 关闭弹窗
    handleClose() {
      this.selectedRows = [];
      this.candidates = [];
      this.$emit("close");
    },
    // 取消
    handleCancel() {
      this.dialogVisible = false;
    },
    // 确认（保存到接口）
    handleConfirm() {
      if (!this.selectedRows || this.selectedRows.length === 0) {
        this.$message.warning("请至少选择一个报名人");
        return;
      }

      // 直接返回选中的行数据，不需要调用创建接口（列表中的项已经存在于后端）
      this.$emit("confirm", this.selectedRows);
      this.dialogVisible = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.select-registrant-body {
}
</style>
